import { getScheduledOpeningTime, getScheduledClosingTime } from './mapper';

const initialState = {
  activeOrders: [],
  completedOrders: [],
  newOrders: undefined,
  newOrder: undefined,
  loading: true,
  error: false,
  rejectInProgress: false,
  orderSelected: false,
  selectedOrder: null,
  selectedOrderNumber: null,
  rejectedOrderId: undefined,
  acceptedOrderId: undefined,
  token: undefined,
  isPolling: false,
  pickupNote: undefined,
  isWebRomLoginSuccessful: false,
  hasMarkedAsOutForDelivery: false,
};

const liveOrdersReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case 'SET_SELECTED_ORDER': {
      return {
        ...state,
        orderSelected: true,
        selectedOrder: action.selectedOrder,
        selectedOrderNumber:
          action.selectedOrder && action.selectedOrder.order_number,
      };
    }
    case 'CLEAR_SELECTED_ORDER': {
      return {
        ...state,
        orderSelected: initialState.orderSelected,
        selectedOrder: initialState.selectedOrder,
        selectedOrderNumber: initialState.selectedOrderNumber,
      };
    }
    case 'GET_ONE_TIME_PASS_REQUEST': {
      return {
        ...state,
        token: initialState.token,
        isWebRomLoginSuccessful: false,
      };
    }
    case 'GET_MENU_ITEMS_REQUEST':
    case 'GET_RESTAURANT_INFO_REQUEST':
    case 'LOGON_WITH_PASS_REQUEST':
    case 'GET_ACTIVE_ORDERS_REQUEST': {
      return {
        ...state,
        loading: true,
        error: initialState.error,
        isPolling: action.extra.isPolling,
      };
    }
    case 'GET_ACTIVE_ORDERS_SUCCESS': {
      const rejectedOrderId =
        action.extra.rejectedOrderId && action.extra.rejectedOrderId.toString();
      const acceptedOrderId =
        action.extra.acceptedOrderId && action.extra.acceptedOrderId.toString();
      const newOrders = action.payload.orders.filter(
        (order) =>
          order.status === 'new' &&
          order.id !== rejectedOrderId &&
          order.id !== acceptedOrderId,
      );
      const newOrdersByTimePlaced = newOrders.sort((a, b) => {
        const first = new Date(a.created_at);
        const second = new Date(b.created_at);
        if (first < second) {
          return -1;
        }
        if (first > second) {
          return 1;
        }
        return 0;
      });
      return {
        ...state,
        loading: false,
        activeOrders: action.payload.orders,
        newOrders: newOrdersByTimePlaced,
        newOrder:
          (!!newOrdersByTimePlaced.length && newOrdersByTimePlaced[0]) ||
          undefined,
      };
    }
    case 'GET_COMPLETED_ORDERS_SUCCESS': {
      return {
        ...state,
        completedOrders: action.payload.orders,
        hasMarkedAsOutForDelivery: false,
      };
    }
    case 'GET_ACTIVE_ORDERS_FAILURE': {
      return {
        ...state,
        loading: false,
        error: true,
      };
    }
    case 'GET_ONE_TIME_PASS_SUCCESS': {
      return {
        ...state,
        loading: false,
        pass: action.payload.pass,
      };
    }
    case 'GET_ONE_TIME_PASS_FAILURE': {
      return {
        ...state,
        loading: false,
        pass: null,
      };
    }
    case 'LOGON_WITH_PASS_SUCCESS': {
      return {
        ...state,
        loading: false,
        token: action.payload && action.payload.token,
        isWebRomLoginSuccessful: true,
      };
    }
    case 'LOGON_WITH_PASS_FAILURE': {
      return {
        ...state,
        loading: false,
        token: initialState.token,
        isWebRomLoginSuccessful: false,
      };
    }
    case 'REJECT_ORDER_REQUEST': {
      return {
        ...state,
        rejectedOrderId: initialState.rejectedOrderId,
      };
    }
    case 'REJECT_ORDER_SUCCESS': {
      return {
        ...state,
        newOrder: undefined,
        rejectInProgress: true,
        rejectedOrderId: action.payload && action.payload.id,
      };
    }
    case 'ACCEPT_ORDER_REQUEST': {
      return {
        ...state,
        acceptedOrderId: initialState.acceptedOrderId,
      };
    }
    case 'ACCEPT_ORDER_SUCCESS': {
      return {
        ...state,
        newOrder: undefined,
        acceptedOrderId: action.payload && action.payload.id,
      };
    }
    case 'GET_RESTAURANT_INFO_SUCCESS': {
      const currentDay = new Date().getDay();
      const shiftsObject = action.payload && action.payload.shifts;
      const shiftsArray = Object.values(shiftsObject);
      const currentDayShifts = shiftsArray[currentDay];
      const nextDayShifts = () => {
        // We need to ensure that the next day index is always between 0 and 6
        // eg if Saturday is index 6, the next day (Sunday) should be index 0
        const nextDayIndex = (currentDay + 1) % 7;
        const shiftsFromStartOfWeekToCurrentDay = shiftsArray.slice(
          0,
          nextDayIndex,
        );
        const shiftsFromCurrentDayToEndOfWeek = shiftsArray.slice(nextDayIndex);
        const shiftsInWeek = shiftsFromCurrentDayToEndOfWeek.concat(
          shiftsFromStartOfWeekToCurrentDay,
        );
        const validNextDayShifts = shiftsInWeek.find((shifts) => shifts.length);
        return validNextDayShifts;
      };
      // eslint-disable-next-line camelcase
      const pickupFulfillment = action.payload?.fulfillment_types?.filter(
        ({ type }) => type === 'customer_collection',
      );
      return {
        ...state,
        loading: false,
        restaurantInfo: action.payload,
        isRestaurantOpen: action.payload && action.payload.status === 'OPEN',
        scheduledOpeningTime: getScheduledOpeningTime(
          currentDayShifts,
          nextDayShifts(),
        ),
        scheduledClosingTime: getScheduledClosingTime(currentDayShifts),
        pickupNote: pickupFulfillment.length && pickupFulfillment[0].note,
        fulfillmentTypes:
          action.payload &&
          action.payload.fulfillment_types &&
          action.payload.fulfillment_types
            .filter((fulfillmentType) => fulfillmentType.enabled)
            .map((fulfillmentType) => fulfillmentType.type),
      };
    }
    case 'GET_RESTAURANT_INFO_FAILURE': {
      return {
        ...state,
        loading: false,
        error: true,
      };
    }
    case 'END_REJECT_IN_PROGRESS': {
      return {
        ...state,
        rejectInProgress: false,
      };
    }
    case 'GET_MENU_ITEMS_SUCCESS': {
      return {
        ...state,
        loading: false,
        menuCategories: action.payload.categories,
      };
    }
    case 'GET_MENU_ITEMS_FAILURE': {
      return {
        ...state,
        loading: false,
        error: true,
      };
    }
    case 'MARK_AS_OUT_FOR_DELIVERY_SUCCESS': {
      return {
        ...state,
        hasMarkedAsOutForDelivery: action.extra?.hasMarkedAsOutForDelivery,
      };
    }
    default:
      return state;
  }
};

export default liveOrdersReducer;
